/**
 * /dashboard/payroll — Payroll runs list (G5.5).
 *
 * HR-admin landing page. The PayrollRoleGate inside PayrollModuleShell
 * is the in-page UX mirror of the backend's RBAC on every /payroll/*
 * route (RequireRole company_admin / hr_manager) — backend stays the
 * boundary; gate is presentation.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/payroll/page.tsx with:
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link
 *   - PayrollRoleGate + PageHeader hoisted into PayrollModuleShell so
 *     the three Payroll routes share one frame.
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { BadgeIndianRupee, FileText, Plus } from "lucide-react";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { RunList } from "@/components/payroll/run-list";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/payroll/")({
  component: PayrollListRoute,
});

function PayrollListRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  return (
    <PayrollModuleShell
      title="Payroll"
      description="Compute monthly payroll inputs and export the vendor CSV (Razorpay X / Zoho Payroll / GreytHR / Keka)."
      actions={
        <div className="flex gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/payroll/statutory" params={{ tenantSlug }}>
              <FileText aria-hidden className="h-4 w-4" />
              Statutory exports
            </Link>
          </Button>
          <Button asChild className="gap-1.5" data-testid="payroll-list-new-button">
            <Link to="/app/$tenantSlug/payroll/new" params={{ tenantSlug }}>
              <Plus aria-hidden className="h-4 w-4" />
              New run
            </Link>
          </Button>
        </div>
      }
    >
      <div className="flex items-center gap-2 text-sm text-muted-foreground sm:hidden">
        <BadgeIndianRupee aria-hidden className="h-4 w-4" />
        <span>HR-admin view</span>
      </div>
      <RunList />
    </PayrollModuleShell>
  );
}
